import React, { useRef, useState, useEffect, useMemo } from 'react'
import { IconButton, ListItem, Tooltip } from '@material-ui/core'
import isEqual from 'lodash/isEqual'
import { connect } from 'react-redux'
import { injectIntl, WrappedComponentProps } from 'react-intl'
import { Map } from 'immutable'
import { ReactComponent as AddAssigneeIcon } from 'assets/img/plus-icon.svg'
import { filterArrBySearchQuery } from 'helpers/common'
import {
  DEFAULT_JOB_ASSIGNEES_TMP_DATA,
  FILTER_TYPE_ASSIGNED_USER,
  FILTER_TYPE_ASSIGNED_USER_GROUP,
} from 'constants/managementUI'
import { UserGroup, User, Site } from 'types/common'
import { Flex } from 'components/common/styles'
import { MenuTitle } from 'components/common/Menu'
import messages from './UsersList.messages'
import AvatarPlaceholder from '../AvatarPlaceholder/AvatarPlaceholder'
import UsersListDropdown from '../UsersListDropdown/UsersListDropdown'
import UsersSelectList from '../UsersSelectList/UsersSelectList'
import {
  AllAssignees,
  RemoveUserButton,
  AssigneeHolder,
  AssigneeImage,
} from './UsersList.styles'
import { UsersListProps } from './UsersList.types'

export const UsersList = ({
  intl: { formatMessage },
  users = [],
  groups = [],
  hideNoAssigneesIcon = false,
  updateJobAssigneesTmpData = DEFAULT_JOB_ASSIGNEES_TMP_DATA,
  onAddUser,
  onRemoveUser,
  filtersList,
  extraAction,
}: WrappedComponentProps & UsersListProps) => {
  const {
    assigned_user: usersList,
    assigned_user_group: userGroupsList,
  } = filtersList.toJS()

  const [usersMenuAnchor, setUsersMenuAnchor] = useState<HTMLElement | null>(
    null,
  )
  const [searchQuery, setSearchQuery] = useState('')
  const [tmpAppliedUsers, setTmpAppliedUsers] = useState<User[]>(users)
  const [tmpAppliedUserGroups, setTmpAppliedUserGroups] = useState<UserGroup[]>(
    groups,
  )
  const [tmpUsers, setTmpUsers] = useState<User[]>([])
  const [tmpUserGroups, setTmpUserGroups] = useState<UserGroup[]>([])

  const searchFieldRef = useRef<HTMLElement>(null)

  useEffect(() => {
    setTmpAppliedUsers(users)
    setTmpUsers([])
  }, [JSON.stringify(users)])

  useEffect(() => {
    setTmpAppliedUserGroups(groups)
    setTmpUserGroups([])
  }, [JSON.stringify(groups)])

  const usersArr = useMemo(
    () => [
      ...users.map(item => ({
        ...item,
        type: FILTER_TYPE_ASSIGNED_USER,
      })),
      ...groups.map(item => ({
        ...item,
        type: FILTER_TYPE_ASSIGNED_USER_GROUP,
      })),
    ],
    [JSON.stringify(users), JSON.stringify(groups)],
  )

  const firstFourUsers = usersArr.slice(0, 4)

  const onOpenUsersMenu = (target: HTMLElement) => {
    setUsersMenuAnchor(target)

    setTimeout(() => {
      if (searchFieldRef.current) {
        searchFieldRef.current.focus()
      }
    }, 0)
  }

  const onCloseUsersMenu = () => {
    const updatedUsers = [...tmpAppliedUsers, ...tmpUsers]
    const updatedUserGroups = [...tmpAppliedUserGroups, ...tmpUserGroups]
    const shouldUpdate =
      !isEqual(users, updatedUsers) ||
      !isEqual(groups, updatedUserGroups) ||
      updateJobAssigneesTmpData.add.users.length ||
      updateJobAssigneesTmpData.add.user_groups.length ||
      updateJobAssigneesTmpData.remove.users.length ||
      updateJobAssigneesTmpData.remove.user_groups.length

    setUsersMenuAnchor(null)
    setSearchQuery('')

    if (shouldUpdate) {
      onAddUser?.(updatedUsers, updatedUserGroups)
    }
  }

  const renderUsersListDropdown = () => {
    const filteredAppliedUsers = filterArrBySearchQuery(users, searchQuery)
    const filteredAppliedUserGroups = filterArrBySearchQuery(
      groups,
      searchQuery,
    )
    const filteredUsers = filterArrBySearchQuery(usersList, searchQuery).filter(
      ({ id }: User) =>
        !filteredAppliedUsers.find(
          ({ id: appliedId }: User) => id === appliedId,
        ),
    )
    const filteredUserGroups = filterArrBySearchQuery(
      userGroupsList,
      searchQuery,
    ).filter(
      ({ id }: UserGroup) =>
        !filteredAppliedUserGroups.find(
          ({ id: appliedId }: UserGroup) => id === appliedId,
        ),
    )

    return (
      <div>
        {(!!filteredAppliedUsers.length ||
          !!filteredAppliedUserGroups.length) && (
          <MenuTitle>{formatMessage(messages.assigned)}</MenuTitle>
        )}
        {!!filteredAppliedUsers.length && (
          <UsersSelectList
            list={filteredAppliedUsers}
            tmpUsersArr={tmpAppliedUsers}
            setTmpUsersArr={setTmpAppliedUsers}
            extraAction={extraAction}
            assigneeType="users"
          />
        )}
        {!!filteredAppliedUserGroups.length && (
          <UsersSelectList
            list={filteredAppliedUserGroups}
            tmpUsersArr={tmpAppliedUserGroups}
            setTmpUsersArr={setTmpAppliedUserGroups}
            extraAction={extraAction}
            assigneeType="user_groups"
          />
        )}
        {!!filteredUsers.length && (
          <>
            <MenuTitle>{formatMessage(messages.users)}</MenuTitle>
            <UsersSelectList
              list={filteredUsers}
              tmpUsersArr={tmpUsers}
              setTmpUsersArr={setTmpUsers}
              extraAction={extraAction}
              assigneeType="users"
            />
          </>
        )}
        {!!filteredUserGroups.length && (
          <>
            <MenuTitle>{formatMessage(messages.teams)}</MenuTitle>
            <UsersSelectList
              list={filteredUserGroups}
              tmpUsersArr={tmpUserGroups}
              setTmpUsersArr={setTmpUserGroups}
              extraAction={extraAction}
              assigneeType="user_groups"
            />
          </>
        )}
        {!filteredAppliedUsers.length &&
          !filteredAppliedUserGroups.length &&
          !filteredUsers.length &&
          !filteredUserGroups.length && (
            <ListItem>{formatMessage(messages.noResultsMessage)}</ListItem>
          )}
      </div>
    )
  }

  const handleOpenUserMenu = ({
    currentTarget,
  }: {
    currentTarget: HTMLElement
  }) => {
    if (onAddUser) {
      onOpenUsersMenu(currentTarget)
    }
  }

  if (!users.length && !groups.length) {
    return (
      <>
        {!hideNoAssigneesIcon && !!onAddUser && (
          <IconButton size="small" onClick={handleOpenUserMenu}>
            <AddAssigneeIcon />
          </IconButton>
        )}
        {hideNoAssigneesIcon && !onAddUser && '-'}
        {!!onAddUser && (
          <UsersListDropdown
            anchor={usersMenuAnchor}
            onCloseMenu={onCloseUsersMenu}
            searchQuery={searchQuery}
            setSearchQuery={setSearchQuery}
            inputRef={searchFieldRef}
            renderUserList={renderUsersListDropdown}
            noTransform
          />
        )}
      </>
    )
  }

  return (
    <>
      <Flex align="center" justify="center" onClick={handleOpenUserMenu}>
        {firstFourUsers.map(({ id, name, avatar, type }) => (
          <AssigneeImage key={id}>
            <Tooltip title={name}>
              {avatar ? (
                <AssigneeHolder>
                  {!!onRemoveUser && (
                    <RemoveUserButton
                      aria-label="remove button"
                      onClick={event => {
                        event.stopPropagation()
                        onRemoveUser(id, type)
                      }}
                    />
                  )}
                  <img src={avatar} alt={name} />
                </AssigneeHolder>
              ) : (
                <AssigneeHolder>
                  {!!onRemoveUser && (
                    <RemoveUserButton
                      aria-label="remove button"
                      onClick={event => {
                        event.stopPropagation()
                        onRemoveUser(id, type)
                      }}
                    />
                  )}
                  <AvatarPlaceholder userName={name} />
                </AssigneeHolder>
              )}
            </Tooltip>
          </AssigneeImage>
        ))}
        {usersArr.length > 4 && (
          <AllAssignees>+{usersArr.length - 4}</AllAssignees>
        )}
      </Flex>
      {!!onAddUser && (
        <UsersListDropdown
          anchor={usersMenuAnchor}
          onCloseMenu={onCloseUsersMenu}
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
          inputRef={searchFieldRef}
          renderUserList={renderUsersListDropdown}
          noTransform
        />
      )}
    </>
  )
}

export default connect(
  (state: {
    getIn: (path: Array<string>) => Map<string, User | UserGroup | Site>
  }) => ({
    filtersList: state.getIn(['managementUI', 'filtersList']),
  }),
)(injectIntl(UsersList))
